import { ApiError, ApiErrorResponse, isApiErrorResponse } from '../types/ApiError';
/**
 * Parses API error response from various formats
 * @param response - The HTTP Response object
 * @returns Parsed ApiErrorResponse
 */
export async function parseApiErrorResponse(response: Response): Promise<ApiErrorResponse> {
  const defaultError: ApiErrorResponse = {
    title: response.statusText || 'Request Failed',
    status: response.status,
    instance: response.url,
  };

  try {
    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json') || contentType.includes('application/problem+json')) {
      const body = await response.json();

      if (isApiErrorResponse(body)) {
        return { ...defaultError, ...body };
      }

      // Handle common error formats
      return {
        ...defaultError,
        title: body.title || body.message || body.error || defaultError.title,
        detail: body.detail || body.details || body.description,
        type: body.type,
        instance: body.instance || defaultError.instance,
      };
    }

    // Handle non-JSON responses
    const text = await response.text();
    if (text) {
      return {
        ...defaultError,
        detail: text.length > 200 ? text.substring(0, 200) + '...' : text,
      };
    }

    return defaultError;
  } catch (parseError) {
    console.warn('Failed to parse error response:', parseError);
    return defaultError;
  }
}

export async function createApiErrorFromResponse(response: Response): Promise<ApiError> {
  const errorResponse = await parseApiErrorResponse(response);
  return new ApiError(errorResponse);
}

export function formatErrorForDisplay(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred';
}

export function logError(error: unknown, context?: string): void {
  const msg = error instanceof ApiError ? error.getDetailedMessage() : error;
  console.error(context ? `[${context}]` : '', msg);
}